import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from '@tanstack/react-router';
import { HTTPError } from 'ky';
import { type ErrorInfo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultError } from '@/components/common/error/DefaultError';
import { logoutViaAuth0 } from '@/components/common/error/GlobalErrorBoundary';
import { TempoApiError } from '@/lib/api/tempoApiTypes';
import { NotFoundError } from './NotFoundError';

interface GlobalErrorProps {
  error?: Error;
  errorInfo?: ErrorInfo;
}

export const GlobalError = ({ error, errorInfo }: GlobalErrorProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  let statusCode: number | undefined;

  if (error instanceof TempoApiError) {
    statusCode = error.error.status;
  } else if (error instanceof HTTPError) {
    statusCode = error.response.status;
  }

  useEffect(() => {
    if (error) {
      // Track the global error in Datadog RUM
      datadogRum.addError(error, {
        source: 'react-error-boundary',
        type: 'global-error',
        stack: error.stack,
        message: error.message,
        componentStack: errorInfo?.componentStack,
      });
    }
  }, [error, errorInfo]);

  if (statusCode === 404) {
    return <NotFoundError />;
  }

  return (
    <DefaultError
      title={t(($) => $.common.errors.global.title)}
      description={t(($) => $.common.errors.global.description)}
      actions={[
        {
          label: t(($) => $.common.goBack),
          action: () => {
            if (router.history.canGoBack()) {
              router.history.back();
            } else {
              router.navigate({ to: '/' });
            }
          },
          variant: 'primary',
        },
        {
          label: t(($) => $.common.logout),
          action: logoutViaAuth0,
          variant: 'secondary',
        },
      ]}
    />
  );
};
